import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Errors, { getString } from "../../../components/errors/Errors";
import Listview from "../../../components/listview/Listview";
import { DefaultPagination, getPagination, Pagination } from "../../../components/listview/Pagination";
import useAbortController from "../../../hooks/useAbortController";
import useStatusBarState from "../../../hooks/useStatusBar";
import { Administration } from "../../../listsSettings/administration";
import { ErrorCode, IAction, IListviewField, SearchResult, UserPermissions } from "../../../services/types";
import { getCurrentUrlAsSource, getSelectedItems } from "../../../services/utils";
import { getUserPermissions } from "../../../services/webapi";
import strings from "../../../strings";
import Add from "../../../svg/Add.svg?react";
import Bin from "../../../svg/Bin.svg?react";
import Edit from "../../../svg/Edit.svg?react";
import * as Fields from "./Fields";
import { GlobalNavigationItemFull } from "./types";
import { editForm, newForm } from "./urls";
import { deleteGlobalNavigationItems, getGlobalNavigationItemFilters, getGlobalNavigationItems } from "./webapi";

const AllItems: FC = () => {
  const [abortController, resetAbortController] = useAbortController();
  const listviewId = "gni-all-items";
  const fields: IListviewField<GlobalNavigationItemFull>[] = [
    Fields.name,
    Fields.url,
    Fields.order,
    Fields.openNewTab,
    Fields.parent,
  ];

  const statusBar = useStatusBarState();
  const [globalNavigationItems, setGlobalNavigationItems] = useState<SearchResult<GlobalNavigationItemFull> | null>(
    null,
  );
  const [errorCode, setErrorCode] = useState<ErrorCode>();

  const [permissions, setPermissions] = useState<UserPermissions>();
  const navigateTo = useNavigate();

  const pagination = getPagination(listviewId) || {
    ...DefaultPagination,
  };

  useEffect(() => {
    loadGlobalNavigationItems(pagination);

    return () => {
      abortController && abortController.abort();
    };
  }, []);

  const loadGlobalNavigationItems = async (pagination: Pagination) => {
    const abortController = resetAbortController();
    const permissions = await getUserPermissions(
      Administration.Lists.GlobalNavigationItems.InternalName,
      Administration.InternalName,
      abortController.signal,
    );
    if (!abortController.signal.aborted) {
      setPermissions(permissions);
      const canRead = permissions.schemaPermission && permissions.listPermissions.includes("read-all");
      !canRead && setErrorCode(403);
      if (canRead) {
        const result = await getGlobalNavigationItems(pagination, abortController.signal);
        if (!abortController.signal.aborted) {
          result.data && setGlobalNavigationItems(result.data);
          result.error && setErrorCode(result.error.code);
        }
      }
    }
  };

  const newAction: IAction = {
    title: strings.new,
    icon: Add,

    onClick: () => navigateTo(`${newForm}?${getCurrentUrlAsSource()}`),
    disabled: !permissions || !permissions.listPermissions.includes("create"),
  };

  const editAction: IAction = {
    title: strings.edit,
    icon: Edit,
    onClick: () => {
      const selectedItem = getSelectedItems(globalNavigationItems)[0];
      selectedItem && navigateTo(`${editForm}/${selectedItem.id}?${getCurrentUrlAsSource()}`);
    },
    disabled:
      getSelectedItems(globalNavigationItems).length !== 1 ||
      !permissions ||
      !permissions.listPermissions.includes("edit-all"),
  };

  const deleteAction: IAction = {
    title: strings.delete,
    icon: Bin,
    onClick: async () => {
      const selectedItems = getSelectedItems(globalNavigationItems);
      if (selectedItems.length > 0 && confirm(strings.confirmDeleteItems)) {
        const abortController = resetAbortController();
        const result = await deleteGlobalNavigationItems(
          selectedItems.map((x) => x.id),
          abortController.signal,
        );

        if (result.error) {
          statusBar.addError(getString(result.error));
        } else if (result.data) {
          const s = selectedItems.filter((x) => x.id && result.data?.[x.id]);

          if (s.length != selectedItems.length) {
            statusBar.addWarning(strings.notAllElementsDeleted);
          }
          if (s.length > 0) {
            await loadGlobalNavigationItems(pagination);
          }
        }
      }
    },
    disabled: getSelectedItems(globalNavigationItems).length === 0 || newAction.disabled,
  };

  const actions = [newAction, editAction, deleteAction];

  return (
    <>
      {permissions &&
        (!errorCode ? (
          <Listview
            id={listviewId}
            actions={actions}
            viewProperties={{
              schema: Administration.InternalName,
              list: Administration.Lists.GlobalNavigationItems.InternalName,
              name: "all_items",
              setListAndPage: true,
            }}
            fields={fields}
            allowSelectRows
            reload={loadGlobalNavigationItems}
            searchResult={globalNavigationItems}
            getFilters={getGlobalNavigationItemFilters}
            pagination={pagination}
            updateItems={(items) =>
              globalNavigationItems &&
              setGlobalNavigationItems({
                ...globalNavigationItems,
                items: items,
              })
            }
          />
        ) : (
          errorCode && <Errors errorCode={errorCode} />
        ))}
    </>
  );
};

export default AllItems;
